/**
 * DeepResearchChatStorage utility
 *
 * This module provides functions to store and retrieve chat IDs in session storage
 * for per-tab chat persistence. Each browser tab maintains its own chat session.
 *
 * The backend supervisor attempts to catch exceptions and record them within the persisted chat; however,
 * some exceptions are not caught, and are surfaced only in the ephemeral task response. In such cases,
 * if a user refreshes the page, they would see a forever "Thinking" progress message instead of an
 * error message. To improve the UX, we persist the status URL to session storage so that we can
 * resume polling after the user refreshes the page.
 *
 * This is a temporary solution to improve the UX. We should use a combination of techniques to provide
 * a better experience:
 *   - Capture more exceptions in the persisted model
 *   - Retry the task if it fails
 *   - Provide a fallback error message if the task has not completed after a certain amount of time *
 */

/** Storage key prefix for DeepResearch */
const DR_STATUS_URL_KEY = 'deep-research-status-url';

/**
 * Get the status URL storage key specific to a chat session.
 */
function getStatusUrlStorageKey(chatId: string): string {
  return `${DR_STATUS_URL_KEY}-${chatId}`;
}

/**
 * Save the current status URL to session storage
 */
export function saveStatusUrl(chatId: string, statusUrl: string): void {
  const storageKey = getStatusUrlStorageKey(chatId);
  sessionStorage.setItem(storageKey, statusUrl);
}

/**
 * Load saved status URL from session storage
 */
export function loadStatusUrl(chatId: string | null): string | undefined {
  if (chatId == null) {
    return undefined;
  }
  const storageKey = getStatusUrlStorageKey(chatId);
  return sessionStorage.getItem(storageKey) || undefined;
}
